import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Avatar from '../components/Avatar';
import ActivityMini from '../components/ActivityMini';
import WeeklyRecapCard from '../components/WeeklyRecapCard';
import TutorialModal from '../components/TutorialModal';
import {
  Plus, Play, PencilSimple, Barbell, Folder, FolderPlus, DotsThreeVertical,
  X, Trash, CaretDown, CaretUp,
} from '@phosphor-icons/react';
import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import {
  DndContext, PointerSensor, TouchSensor, useSensor, useSensors,
  useDraggable, useDroppable, DragOverlay,
} from '@dnd-kit/core';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Today() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [fatigue, setFatigue] = useState({ last_hit: {} });
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [selectedDay, setSelectedDay] = useState(new Date().toDateString());
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [dragging, setDragging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);

  // Show onboarding tutorial on first login (via SignIn) or if user hasn't
  // marked it seen. localStorage flag beats the server flag for guests.
  useEffect(() => {
    if (!user) return;
    const flag = localStorage.getItem('breth_show_tutorial');
    if (flag === '1' || (!user.tutorial_seen && user.total_workouts === 0 && flag !== '0')) {
      setShowTutorial(true);
    }
  }, [user]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [pr, ro, ac, ex, fat] = await Promise.all([
        axios.get(`${API_URL}/programs`, { withCredentials: true }),
        axios.get(`${API_URL}/routines`, { withCredentials: true }),
        axios.get(`${API_URL}/workouts/active`, { withCredentials: true }),
        axios.get(`${API_URL}/exercises`, { withCredentials: true }),
        axios.get(`${API_URL}/progress/fatigue`, { withCredentials: true }),
      ]);
      setPrograms(pr.data);
      setRoutines(ro.data);
      setActiveWorkout(ac.data);
      setAllExercises(ex.data);
      setFatigue(fat.data || { last_hit: {} });
    } finally { setLoading(false); }
  };

  // Highest fatigue tier across all muscle groups this routine hits (0/1/2)
  const routineFatigueTier = (routine) => {
    if (!routine.exercises || routine.exercises.length === 0) return 0;
    const now = Date.now();
    let maxTier = 0;
    for (const rex of routine.exercises) {
      const ex = allExercises.find((e) => e.id === rex.exercise_id);
      if (!ex) continue;
      const iso = fatigue.last_hit?.[ex.muscle_group];
      if (!iso) continue;
      const hrs = Math.max(0, (now - new Date(iso).getTime()) / 36e5);
      const tier = hrs < 24 ? 2 : hrs < 72 ? 1 : 0;
      if (tier > maxTier) maxTier = tier;
    }
    return maxTier;
  };

  const createProgram = async (name) => {
    await axios.post(`${API_URL}/programs`, { name }, { withCredentials: true });
    await loadData();
    setShowProgramForm(false);
  };

  const deleteProgram = async (id) => {
    if (!window.confirm('Delete this program? Routines inside will move to Ungrouped.')) return;
    await axios.delete(`${API_URL}/programs/${id}`, { withCredentials: true });
    await loadData();
    setEditingProgram(null);
  };

  const startRoutine = async (routine) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/workouts/start`,
        { routine_id: routine.id },
        { withCredentials: true }
      );
      navigate(`/session/${data.id}`);
    } catch (e) {
      console.error(e);
      alert('Could not start workout');
    }
  };

  const assignRoutineToProgram = async (routineId, programId) => {
    // Optimistic
    setRoutines((prev) => prev.map((r) => r.id === routineId ? { ...r, program_id: programId } : r));
    try {
      await axios.put(
        `${API_URL}/routines/${routineId}`,
        { program_id: programId }, // null = ungroup
        { withCredentials: true }
      );
    } catch (e) {
      console.error(e);
      loadData(); // revert
    }
  };

  const onDragStart = (evt) => setDragging(evt.active.id);
  const onDragEnd = (evt) => {
    setDragging(null);
    const { active, over } = evt;
    if (!over) return;
    const routineId = active.id;
    // over.id is a droppable id: `prog-<id>` or `ungrouped`
    const overId = over.id;
    const targetProgramId = overId === 'ungrouped' ? null : overId.replace('prog-', '');
    const routine = routines.find((r) => r.id === routineId);
    if (!routine) return;
    if ((routine.program_id || null) === (targetProgramId || null)) return;
    assignRoutineToProgram(routineId, targetProgramId);
  };

  const routinesByProgram = useMemo(() => {
    const byProg = {};
    const ungrouped = [];
    routines.forEach((r) => {
      if (r.program_id) (byProg[r.program_id] = byProg[r.program_id] || []).push(r);
      else ungrouped.push(r);
    });
    return { byProg, ungrouped };
  }, [routines]);

  const draggedRoutine = dragging ? routines.find((r) => r.id === dragging) : null;

  const getWeekDays = () =>
    Array.from({ length: 7 }, (_, i) =>
      addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), i)
    );

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  return (
    <div className="mobile-container min-h-screen">
      <div className="content-wrapper px-5 pt-10">
        {/* Header */}
        <div className="mb-6 animate-slide-up">
          <p className="text-xs uppercase tracking-[0.24em] text-graphite/50 font-semibold mb-1">
            {format(new Date(), 'EEEE')} · {format(new Date(), 'MMMM d')}
          </p>
          <h1 className="text-6xl font-black font-heading uppercase tracking-tight text-graphite leading-none">
            Hey, {user.display_name?.split(' ')[0] || 'Lifter'}
          </h1>
        </div>

        {/* Rolling activity view — replaces the flat M-T-W week strip */}
        <ActivityMini />

        {/* Sunday-only Weekly Recap card */}
        <WeeklyRecapCard />

        {/* Active session banner */}
        {activeWorkout && (
          <div className="rounded-ios-lg p-5 mb-6 animate-pop-in shadow-ios active-workout-card overflow-hidden" style={{ backgroundColor: '#C0392F', color: '#EDEAE3' }} data-testid="active-session-banner">
            <span aria-hidden className="drip drip-1" />
            <span aria-hidden className="drip drip-2" />
            <span aria-hidden className="drip drip-3" />
            <span aria-hidden className="drip drip-4" />
            <span aria-hidden className="drip drip-5" />
            <p className="relative text-[10px] uppercase tracking-[0.24em] text-bone/70 font-bold mb-1">
              In Progress
            </p>
            <div className="relative flex items-center justify-between">
              <p className="text-2xl font-black font-heading uppercase text-bone truncate">
                {activeWorkout.routine_name || 'Session Running'}
              </p>
              <button
                onClick={() => navigate(`/session/${activeWorkout.id}`)}
                className="h-12 px-5 bg-bone text-userRed font-black text-sm uppercase tracking-wider rounded-ios-pill flex items-center gap-2"
                data-testid="resume-session-button"
              >
                <Play size={18} weight="fill" />
                Resume
              </button>
            </div>
          </div>
        )}

        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          {/* PROGRAMS SECTION */}
          <div className="flex items-baseline justify-between mb-3">
            <div className="flex items-baseline gap-2">
              <h2 className="text-2xl font-black font-heading uppercase text-graphite">
                Programs
              </h2>
              {programs.length > 0 && (
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-graphite/40 tabular-nums">
                  {programs.length}
                </span>
              )}
            </div>
            <button
              onClick={() => setShowProgramForm(true)}
              className="text-xs uppercase tracking-[0.15em] font-bold text-userRed hover:opacity-70 flex items-center gap-1"
              data-testid="new-program-button"
            >
              <FolderPlus size={14} weight="bold" /> New
            </button>
          </div>

          {programs.length === 0 ? (
            <div className="mb-6 px-1">
              <p className="text-xs text-graphite/50">
                No programs yet — group your routines into folders like "Push/Pull/Legs Week" or "Cutting Phase".
              </p>
            </div>
          ) : (
            <div className="space-y-3 mb-8">
              {programs.map((prog) => (
                <ProgramCard
                  key={prog.id}
                  program={prog}
                  routines={routinesByProgram.byProg[prog.id] || []}
                  collapsed={!!collapsed[prog.id]}
                  onToggle={() => setCollapsed({ ...collapsed, [prog.id]: !collapsed[prog.id] })}
                  onStart={startRoutine}
                  onEdit={() => setEditingProgram(prog)}
                  disabled={!!activeWorkout}
                  isDraggingSomething={!!dragging}
                  fatigueTierFor={routineFatigueTier}
                />
              ))}
            </div>
          )}

          {/* UNGROUPED / ALL ROUTINES */}
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-2xl font-black font-heading uppercase text-graphite">
              {programs.length > 0 ? 'Ungrouped' : 'Routines'}
            </h2>
            <button
              onClick={() => navigate('/routine/new')}
              className="text-xs uppercase tracking-[0.15em] font-bold text-userRed hover:opacity-70 flex items-center gap-1"
              data-testid="create-routine-button"
            >
              <Plus size={14} weight="bold" /> New Routine
            </button>
          </div>

          {routines.length === 0 ? (
            <div className="ios-card p-8 text-center">
              <Barbell size={40} weight="fill" className="text-graphite/30 mx-auto mb-3" />
              <h3 className="text-xl font-black font-heading uppercase text-graphite mb-2">
                Build Your First Routine
              </h3>
              <p className="text-sm text-graphite/60 mb-5">
                Pick your lifts, set your targets, and hit send it.
              </p>
              <button
                onClick={() => navigate('/routine/new')}
                className="h-14 px-8 bg-userRed text-bone font-black text-sm uppercase tracking-wider rounded-ios-pill"
                data-testid="empty-create-routine-button"
              >
                Create Routine
              </button>
            </div>
          ) : (
            <UngroupedDropZone isDraggingSomething={!!dragging}>
              {routinesByProgram.ungrouped.length === 0 ? (
                <p className="text-xs text-graphite/40 italic text-center py-3">
                  {dragging ? 'Drop here to remove from a program' : 'All routines are grouped into programs above'}
                </p>
              ) : (
                <div className="space-y-3">
                  {routinesByProgram.ungrouped.map((r, idx) => (
                    <DraggableRoutineCard
                      key={r.id}
                      routine={r}
                      onStart={() => startRoutine(r)}
                      onEdit={() => navigate(`/routine/${r.id}/edit`)}
                      disabled={!!activeWorkout}
                      animationDelay={idx * 40}
                      fatigueTier={routineFatigueTier(r)}
                    />
                  ))}
                </div>
              )}
            </UngroupedDropZone>
          )}

          <DragOverlay dropAnimation={null}>
            {draggedRoutine ? <RoutineCardBody routine={draggedRoutine} floating /> : null}
          </DragOverlay>
        </DndContext>
      </div>

      {showProgramForm && (
        <ProgramFormModal
          onCreate={createProgram}
          onClose={() => setShowProgramForm(false)}
        />
      )}

      {editingProgram && (
        <ProgramManageSheet
          program={editingProgram}
          onDelete={() => deleteProgram(editingProgram.id)}
          onClose={() => setEditingProgram(null)}
        />
      )}

      {showTutorial && (
        <TutorialModal onClose={() => setShowTutorial(false)} source="home" />
      )}
</div>
  );
}

/* ------------------------ Program card ------------------------ */

function ProgramCard({ program, routines, collapsed, onToggle, onStart, onEdit, disabled, isDraggingSomething, fatigueTierFor }) {
  const { setNodeRef, isOver } = useDroppable({ id: `prog-${program.id}` });
  return (
    <div
      ref={setNodeRef}
      className={`ios-card overflow-hidden transition-all ${
        isOver ? 'border-userRed ring-2 ring-userRed/40 bg-userRed/6' : ''
      } ${isDraggingSomething && !isOver ? 'border-dashed' : ''}`}
      data-testid={`program-card-${program.id}`}
    >
      <div className="px-4 py-3 flex items-center gap-2 border-b border-hairline/50 bg-graphite/[0.02]">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 text-left"
          data-testid={`program-toggle-${program.id}`}
        >
          <Folder size={16} weight="fill" className="text-userRed" />
          <p className="text-base font-black font-heading uppercase text-graphite truncate">
            {program.name}
          </p>
          <span className="text-xs font-semibold text-graphite/50 tabular-nums">
            {routines.length}
          </span>
          <span className="ml-auto text-graphite/40">
            {collapsed ? <CaretDown size={16} weight="bold" /> : <CaretUp size={16} weight="bold" />}
          </span>
        </button>
        <button
          onClick={onEdit}
          className="w-9 h-9 rounded-ios-pill hover:bg-graphite/5 flex items-center justify-center text-graphite/50 hover:text-graphite"
          data-testid={`program-menu-${program.id}`}
          aria-label="Program options"
        >
          <DotsThreeVertical size={18} weight="bold" />
        </button>
      </div>
      {!collapsed && (
        <div className="px-3 py-3 space-y-2">
          {routines.length === 0 ? (
            <p className="text-xs text-graphite/40 italic text-center py-4">
              {isDraggingSomething ? 'Drop a routine here to add it' : 'Empty — drag a routine here or set it from the routine editor'}
            </p>
          ) : (
            routines.map((r, idx) => (
              <DraggableRoutineCard
                key={r.id}
                routine={r}
                onStart={() => onStart(r)}
                onEdit={() => (window.location.href = `/routine/${r.id}/edit`)}
                disabled={disabled}
                animationDelay={idx * 30}
                fatigueTier={fatigueTierFor ? fatigueTierFor(r) : 0}
                compact
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function UngroupedDropZone({ children, isDraggingSomething }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'ungrouped' });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-ios-lg transition-all ${
        isOver ? 'ring-2 ring-userRed/40 bg-userRed/6 p-2' : isDraggingSomething ? 'p-2 border border-dashed border-graphite/20' : ''
      }`}
      data-testid="ungrouped-drop-zone"
    >
      {children}
    </div>
  );
}

/* ------------------------ Draggable routine ------------------------ */

function DraggableRoutineCard({ routine, onStart, onEdit, disabled, animationDelay, compact, fatigueTier = 0 }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: routine.id });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="animate-slide-up"
    >
      <RoutineCardBody
        routine={routine}
        onStart={onStart}
        onEdit={onEdit}
        disabled={disabled}
        animationDelay={animationDelay}
        dragHandleProps={{ ...listeners, ...attributes }}
        compact={compact}
        fatigueTier={fatigueTier}
      />
    </div>
  );
}

function RoutineCardBody({ routine, onStart, onEdit, disabled, animationDelay = 0, dragHandleProps, compact, floating, fatigueTier = 0 }) {
  const exerciseCount = routine.exercises?.length || 0;
  const totalSets = routine.exercises?.reduce((a, e) => a + (e.target_sets || 0), 0) || 0;

  // Fatigue rim — subtle red inset shadow, doesn't disturb the ios-card layout
  const fatigueStyle = fatigueTier === 2
    ? { boxShadow: 'inset 0 0 0 1.5px rgba(192,57,47,0.55)' }
    : fatigueTier === 1
      ? { boxShadow: 'inset 0 0 0 1px rgba(192,57,47,0.30)' }
      : {};

  return (
    <div
      className={`ios-card overflow-hidden ${floating ? 'shadow-ios rotate-1' : ''}`}
      style={{ animationDelay: `${animationDelay}ms`, animationFillMode: 'backwards', ...fatigueStyle }}
      data-testid={`routine-card-${routine.id}`}
      data-fatigue={fatigueTier}
    >
      {fatigueTier > 0 && (
        <div
          className={`px-4 py-1 text-[9px] uppercase tracking-[0.22em] font-bold border-b ${
            fatigueTier === 2
              ? 'bg-userRed/8 text-userRed border-userRed/20'
              : 'bg-userRed/[0.04] text-userRed/85 border-userRed/15'
          }`}
          data-testid={`routine-fatigue-hint-${routine.id}`}
        >
          {fatigueTier === 2 ? 'Muscles fresh from today\'s session' : 'Some muscles still recovering'}
        </div>
      )}
      <div className={compact ? 'p-3' : 'p-4'}>
        <div className="flex items-start gap-2">
          {/* Drag handle */}
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              className="w-8 h-8 rounded-ios-pill bg-graphite/5 flex items-center justify-center text-graphite/40 touch-none cursor-grab active:cursor-grabbing"
              aria-label="Drag to move to a program"
              data-testid={`drag-handle-${routine.id}`}
            >
              <DotsThreeVertical size={16} weight="bold" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-graphite/50 font-semibold truncate">
                {routine.group_id ? 'Squad' : 'Personal'}
              </p>
              {routine.owner && (
                <>
                  <Avatar
                    user={routine.owner}
                    size={14}
                    tone="blue"
                    testId={`routine-owner-avatar-${routine.id}`}
                    className="!shadow-none"
                  />
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-partnerBlue truncate">
                    {routine.owner.display_name}
                  </span>
                </>
              )}
            </div>
            <h3 className="text-lg font-black font-heading uppercase text-graphite tracking-tight leading-tight truncate">
              {routine.name}
            </h3>
            <p className="text-[10px] uppercase tracking-[0.15em] text-graphite/50 font-semibold mt-0.5">
              {exerciseCount} lifts · {totalSets} sets
            </p>
          </div>
          <button
            onClick={onStart}
            disabled={disabled || exerciseCount === 0}
            className={`${compact ? 'h-10 px-4 text-xs' : 'h-11 px-4 text-xs'} bg-userRed text-bone font-black uppercase tracking-[0.15em] rounded-ios-pill flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid={`send-it-button-${routine.id}`}
          >
            <Play size={14} weight="fill" />
            Send It
          </button>
          <button
            onClick={onEdit}
            className={`${compact ? 'w-10 h-10' : 'w-11 h-11'} border border-graphite/25 text-graphite rounded-ios-pill flex items-center justify-center hover:bg-graphite hover:text-bone`}
            data-testid={`edit-routine-button-${routine.id}`}
            aria-label="Edit routine"
          >
            <PencilSimple size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------ Program create/edit modals ------------------------ */

function ProgramFormModal({ onCreate, onClose }) {
  const [name, setName] = useState('');
  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
        <div className="px-5 py-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-black font-heading uppercase text-graphite">
              New Program
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"
              data-testid="cancel-program-button"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Push/Pull/Legs Week"
            className="w-full h-14 px-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base mb-4"
            data-testid="program-name-input"
            autoFocus
          />
          <button
            onClick={() => name.trim() && onCreate(name.trim())}
            disabled={!name.trim()}
            className="w-full h-14 bg-userRed text-bone font-bold text-sm uppercase tracking-[0.15em] rounded-ios-pill disabled:opacity-40"
            data-testid="create-program-button"
          >
            Create Program
          </button>
        </div>
      </div>
    </div>
  );
}

function ProgramManageSheet({ program, onDelete, onClose }) {
  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
        <div className="px-5 py-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50">
                Program
              </p>
              <h2 className="text-2xl font-black font-heading uppercase text-graphite truncate">
                {program.name}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"
              data-testid="close-program-sheet-button"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
          <p className="text-sm text-graphite/60">
            Deleting a program keeps every routine — they just move back to Ungrouped.
          </p>
          <button
            onClick={onDelete}
            className="w-full h-14 bg-userRed/12 text-userRed font-black text-sm uppercase tracking-[0.15em] rounded-ios-pill flex items-center justify-center gap-2 hover:bg-userRed hover:text-bone"
            data-testid={`delete-program-${program.id}`}
          >
            <Trash size={16} weight="bold" />
            Delete Program
          </button>
          <button
            onClick={onClose}
            className="w-full h-12 text-xs uppercase tracking-[0.15em] font-bold text-graphite/60 hover:text-graphite"
            data-testid="cancel-program-sheet-button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
